import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar, Button, IconButton, CircularProgress, Chip,
  Divider, InputAdornment, List, ListItem, Dialog,
  DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  AssessmentOutlined as AssessmentIcon,
  ArrowBack as ArrowBackIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  InfoOutlined as InfoIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

const MEDIA_URL = import.meta.env.VITE_MEDIA_URL || '/media/';

interface Course {
  id: number;
  moodle_id: number;
  short_name: string;
  full_name: string;
  category_id: number;
  category_name: string;
  summary: string;
  start_date: string | null;
  end_date: string | null;
  visible: boolean;
  last_sync: string;
}

interface Enrollment {
  id: number;
  user: number;
  user_email: string;
  user_name: string;
  role: string;
  time_enrolled: string | null;
  avatar_url?: string;
}

interface Grade {
  id: number;
  enrollment: number;
  item_name: string;
  item_type: string;
  grade: number | null;
  grade_raw: number | null;
  grade_min: number;
  grade_max: number;
  feedback: string;
  date_graded: string | null;
  user_email: string;
  course_name: string;
}

interface StudentGradeSummary {
  studentId: number;
  name: string;
  email: string;
  enrollmentId: number;
  grades: Grade[];
  average: number | null;
  gradedCount: number;
}

export const CoursesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Course catalog states
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Selected course details state
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Expanded student in gradebook
  const [expandedStudentId, setExpandedStudentId] = useState<number | null>(null);

  // Manual Grade Dialog states
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
  const [editingGrade, setEditingGrade] = useState<Grade | null>(null);
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeForm, setGradeForm] = useState({
    item_name: '',
    grade: '',
    feedback: ''
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch all courses
  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await apiClient.get('/moodle/courses/');
      if (response.data && response.data.results) {
        setCourses(response.data.results);
        setFilteredCourses(response.data.results);
        if (response.data.results.length > 0) {
          setSelectedCourseId(response.data.results[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setToast({ open: true, message: 'No se pudieron cargar los cursos.', severity: 'error' });
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Fetch details (enrollments and grades) for the selected course
  const fetchCourseDetails = async (courseId: number) => {
    setLoadingDetails(true);
    try {
      // 1. Fetch Enrollments
      const enrollmentsRes = await apiClient.get(`/moodle/enrollments/?course=${courseId}`);
      let enrolsList: Enrollment[] = [];
      if (enrollmentsRes.data && enrollmentsRes.data.results) {
        enrolsList = enrollmentsRes.data.results.map((en: any) => ({
          ...en,
          avatar_url: en.user ? `${MEDIA_URL}avatars/avatar_moodle_${en.user}.jpg` : undefined
        }));
        setEnrollments(enrolsList);
      }

      // 2. Fetch Grades
      const gradesRes = await apiClient.get(`/moodle/grades/?enrollment__course=${courseId}`);
      if (gradesRes.data && gradesRes.data.results) {
        setGrades(gradesRes.data.results);
      }
    } catch (err) {
      console.error('Failed to load course details:', err);
      setToast({ open: true, message: 'Fallo al cargar alumnos y calificaciones del curso.', severity: 'error' });
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (selectedCourseId !== null) {
      fetchCourseDetails(selectedCourseId);
      setTabValue(0); // Reset to overview tab
      setExpandedStudentId(null);
    }
  }, [selectedCourseId]);

  // Handle searching courses
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = courses.filter(
      c => c.full_name.toLowerCase().includes(query) || 
           c.short_name.toLowerCase().includes(query) ||
           (c.category_name && c.category_name.toLowerCase().includes(query))
    );
    setFilteredCourses(filtered);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Compile Grade summaries for students in the gradebook tab
  const getStudentGradeSummaries = (): StudentGradeSummary[] => {
    const studentEnrols = enrollments.filter(e => e.role === 'student');
    
    return studentEnrols.map(enrol => {
      const studentGrades = grades.filter(g => g.enrollment === enrol.id);
      const gradedItems = studentGrades.filter(g => g.grade !== null);
      
      // Calculate student course average
      let average: number | null = null;
      if (gradedItems.length > 0) {
        const sum = gradedItems.reduce((acc, curr) => acc + (curr.grade || 0), 0);
        average = Math.round((sum / gradedItems.length) * 10) / 10;
      }

      return {
        studentId: enrol.user,
        name: enrol.user_name,
        email: enrol.user_email,
        enrollmentId: enrol.id,
        grades: studentGrades,
        average,
        gradedCount: gradedItems.length
      };
    });
  };

  // Calculate Course aggregated statistics
  const getCourseAnalytics = () => {
    const summaries = getStudentGradeSummaries();
    const activeStudents = summaries.length;
    
    const validAverages = summaries.map(s => s.average).filter((avg): avg is number => avg !== null);
    
    let courseAvg = 0;
    let highestGrade = 0;
    let lowestGrade = 100;
    let passRate = 0;

    if (validAverages.length > 0) {
      courseAvg = Math.round((validAverages.reduce((acc, val) => acc + val, 0) / validAverages.length) * 10) / 10;
      highestGrade = Math.max(...validAverages);
      lowestGrade = Math.min(...validAverages);
      
      // Pass threshold is 50/100
      const passedCount = validAverages.filter(avg => avg >= 50).length;
      passRate = Math.round((passedCount / validAverages.length) * 100);
    }

    // Compile grade ranges for Recharts
    const ranges = [
      { name: 'Insuficiente (<50)', value: 0, fill: '#FF5252' },
      { name: 'Regular (50-69)', value: 0, fill: '#FF9F43' },
      { name: 'Bueno (70-89)', value: 0, fill: '#0070E0' },
      { name: 'Excelente (90-100)', value: 0, fill: '#00D9A5' }
    ];

    validAverages.forEach(avg => {
      if (avg < 50) ranges[0].value += 1;
      else if (avg < 70) ranges[1].value += 1;
      else if (avg < 90) ranges[2].value += 1;
      else ranges[3].value += 1;
    });

    return {
      activeStudents,
      courseAvg: validAverages.length > 0 ? courseAvg : '-',
      highestGrade: validAverages.length > 0 ? highestGrade : '-',
      lowestGrade: validAverages.length > 0 ? lowestGrade : '-',
      passRate: validAverages.length > 0 ? `${passRate}%` : '-',
      gradeRanges: ranges
    };
  };

  // Grade color formatting
  const getGradeColor = (grade: number | null) => {
    if (grade === null) return '#CBD5E1';
    if (grade >= 90) return '#00D9A5'; // Excellent
    if (grade >= 70) return '#0070E0'; // Good
    if (grade >= 50) return '#FF9F43'; // Regular
    return '#FF5252'; // Insufficient
  };

  // Custom grade item chips helper
  const getGradeTypeChip = (itemType: string) => {
    const typeLower = itemType ? itemType.toLowerCase() : '';
    if (typeLower.includes('tarea') || typeLower.includes('assignment')) {
      return { label: 'Tarea', color: '#0070E0', bgColor: 'rgba(0, 112, 224, 0.06)' };
    }
    if (typeLower.includes('cuestionario') || typeLower.includes('quiz') || typeLower.includes('examen') || typeLower.includes('evaluación')) {
      return { label: 'Cuestionario', color: '#7C4DFF', bgColor: 'rgba(124, 77, 255, 0.06)' };
    }
    if (typeLower.includes('manual')) {
      return { label: 'Manual', color: '#FF9F43', bgColor: 'rgba(255, 159, 67, 0.06)' };
    }
    if (typeLower.includes('cálculo total') || typeLower.includes('total') || typeLower.includes('promedio')) {
      return { label: 'Total', color: '#00D9A5', bgColor: 'rgba(0, 217, 165, 0.06)' };
    }
    return { label: itemType === 'grade_item' ? 'Evaluación' : itemType, color: '#64748B', bgColor: 'rgba(100, 116, 139, 0.06)' };
  };

  // Dialog actions for manual grading
  const handleOpenCreateGrade = (enrollmentId: number) => {
    setSelectedEnrollmentId(enrollmentId);
    setEditingGrade(null);
    setGradeForm({
      item_name: '',
      grade: '',
      feedback: ''
    });
    setGradeDialogOpen(true);
  };

  const handleOpenEditGrade = (enrollmentId: number, grade: Grade) => {
    setSelectedEnrollmentId(enrollmentId);
    setEditingGrade(grade);
    setGradeForm({
      item_name: grade.item_name,
      grade: grade.grade !== null ? String(grade.grade) : '',
      feedback: grade.feedback || ''
    });
    setGradeDialogOpen(true);
  };

  const handleGradeFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGradeForm(prev => ({ ...prev, [name]: value }));
  };

  const handleGradeFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEnrollmentId) return;
    setSavingGrade(true);

    try {
      const payload = {
        enrollment: selectedEnrollmentId,
        item_name: gradeForm.item_name,
        grade: gradeForm.grade !== '' ? parseFloat(gradeForm.grade) : null,
        feedback: gradeForm.feedback,
        item_type: editingGrade ? editingGrade.item_type : 'manual',
        grade_min: 0,
        grade_max: 100
      };

      if (editingGrade) {
        await apiClient.patch(`/moodle/grades/${editingGrade.id}/`, {
          grade: payload.grade,
          feedback: payload.feedback
        });
        setToast({ open: true, message: 'Calificación editada e incorporada a la bitácora de auditoría.', severity: 'success' });
      } else {
        await apiClient.post('/moodle/grades/', payload);
        setToast({ open: true, message: 'Nota manual cargada e incorporada a la bitácora de auditoría.', severity: 'success' });
      }

      setGradeDialogOpen(false);
      // Refresh details
      if (selectedCourseId) {
        await fetchCourseDetails(selectedCourseId);
      }
    } catch (err: any) {
      console.error(err);
      const errMsg = err.response?.data?.detail || 'Error al guardar la calificación.';
      setToast({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setSavingGrade(false);
    }
  };

  // Excel exports
  const handleExportCourseStudents = () => {
    const selectedCourse = courses.find(c => c.id === selectedCourseId);
    if (!selectedCourse) return;
    
    const headers = ['ID Moodle Usuario', 'Nombre Alumno', 'Correo Electrónico', 'Rol Moodle', 'Fecha Inscripción'];
    const keys = ['user', 'user_name', 'user_email', 'role', 'time_enrolled'];
    exportToExcel(enrollments, headers, keys, `Alumnos_${selectedCourse.short_name}`);
  };

  const handleExportCourseGrades = () => {
    const selectedCourse = courses.find(c => c.id === selectedCourseId);
    if (!selectedCourse) return;
    
    const headers = ['Nombre Alumno', 'Correo Electrónico', 'Evaluación', 'Tipo', 'Calificación', 'Min', 'Max', 'Retroalimentación', 'Fecha'];
    const keys = ['user_name', 'user_email', 'item_name', 'item_type', 'grade', 'grade_min', 'grade_max', 'feedback', 'date_graded'];
    
    const flatGrades = grades.map(g => {
      const enrol = enrollments.find(e => e.id === g.enrollment);
      return {
        ...g,
        user_name: enrol ? enrol.user_name : '-',
        user_email: enrol ? enrol.user_email : '-'
      };
    });

    exportToExcel(flatGrades, headers, keys, `Calificaciones_${selectedCourse.short_name}`);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const selectedCourse = courses.find(c => c.id === selectedCourseId);
  const analytics = getCourseAnalytics();
  const studentGradeSummaries = getStudentGradeSummaries();
  const isStaffOrAdmin = user && (user.role === 'admin' || user.role === 'coordinator');

  return (
    <Container maxWidth="xl" className="fade-in-up" sx={{ py: 4, position: 'relative' }}>
      {/* Dynamic Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,112,224,0.03) 0%, rgba(124,77,255,0.01) 75%)',
          filter: 'blur(60px)',
          zIndex: -1,
          animation: 'breathing-nebula 10s ease-in-out infinite'
        }}
      />

      {/* Header Banner */}
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={4} gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={850} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#0A2540', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />
              Explorador de Cursos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supervisión de aulas virtuales, bitácora de participantes y auditoría académica local
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT COLUMN: COURSE SELECTION CATALOG */}
        <Grid item xs={12} md={4} lg={3.5}>
          <Card variant="outlined" sx={{ borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={750} sx={{ color: '#0A2540', mb: 2 }}>
                Aulas Sincronizadas
              </Typography>
              <TextField
                fullWidth
                placeholder="Buscar curso o categoría..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 }
                }}
              />
              
              {loadingCourses ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={30} color="secondary" />
                </Box>
              ) : filteredCourses.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron aulas coincidentes.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: '60vh', overflow: 'auto', pr: 0.5, py: 0 }}>
                  {filteredCourses.map(course => (
                    <ListItem 
                      key={course.id}
                      disablePadding
                      sx={{ mb: 1 }}
                    >
                      <Paper
                        variant="outlined"
                        onClick={() => setSelectedCourseId(course.id)}
                        sx={{
                          width: '100%',
                          p: 1.8,
                          cursor: 'pointer',
                          borderRadius: 3,
                          borderColor: selectedCourseId === course.id ? 'secondary.main' : '#E2E8F0',
                          borderWidth: selectedCourseId === course.id ? '2px' : '1px',
                          bgcolor: selectedCourseId === course.id ? 'rgba(0, 112, 224, 0.02)' : '#FFFFFF',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(10, 37, 64, 0.04)'
                          }
                        }}
                      >
                        <Typography variant="caption" color="secondary" fontWeight={750} sx={{ tracking: 1, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                          {course.short_name}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={750} sx={{ color: '#0A2540', mt: 0.2, lineHeight: 1.2 }}>
                          {course.full_name}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={2.5}>
                          {course.category_name && (
                            <Chip 
                              label={course.category_name} 
                              size="small" 
                              sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'rgba(226, 232, 240, 0.5)', color: '#475569', fontWeight: 600 }} 
                            />
                          )}
                          {!course.visible && (
                            <Chip 
                              icon={<VisibilityOffIcon sx={{ fontSize: '10px !important' }} />}
                              label="Oculto" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }} 
                            />
                          )}
                        </Box>
                      </Paper>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT COLUMN: SELECTED COURSE DETAIL PORTAL */}
        <Grid item xs={12} md={8} lg={8.5}>
          {loadingCourses ? (
            <Card variant="outlined" sx={{ p: 6, display: 'flex', justifyContent: 'center', borderRadius: 4 }}>
              <CircularProgress color="secondary" />
            </Card>
          ) : !selectedCourse ? (
            <Card variant="outlined" sx={{ p: 8, textAlign: 'center', borderRadius: 4 }}>
              <SchoolIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay asignaturas sincronizadas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ejecuta una sincronización completa en Moodle para visualizar las asignaturas.
              </Typography>
            </Card>
          ) : (
            <Box>
              {/* Selected Course Core Card */}
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.75)' }}>
                <CardContent sx={{ p: 3.5 }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="caption" color="secondary" fontWeight={800} sx={{ tracking: 1.5, textTransform: 'uppercase' }}>
                        AULA VIRTUAL ACTIVA
                      </Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ color: '#0A2540', mt: 0.5, mb: 1, fontSize: { xs: '1.3rem', md: '1.7rem' } }}>
                        {selectedCourse.full_name}
                      </Typography>
                      
                      <Box display="flex" gap={1.2} mt={2.5} flexWrap="wrap">
                        <Chip label={`Código: ${selectedCourse.short_name}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                        <Chip label={`Moodle ID: ${selectedCourse.moodle_id}`} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                        {selectedCourse.category_name && <Chip label={`Categoría: ${selectedCourse.category_name}`} size="small" sx={{ fontWeight: 600 }} />}
                        <Chip 
                          label={selectedCourse.visible ? 'Visible en Moodle' : 'Oculto en Moodle'} 
                          icon={selectedCourse.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          size="small"
                          color={selectedCourse.visible ? 'success' : 'warning'}
                          sx={{ fontWeight: 600 }} 
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} justifyContent="center">
                      <Typography variant="caption" color="text.secondary">
                        Sincronización CreaSync
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={750} sx={{ color: '#0A2540', mt: 0.5 }}>
                        {new Date(selectedCourse.last_sync).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Tabs Selector */}
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    indicatorColor="secondary" 
                    textColor="secondary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Resumen y Analítica" icon={<AssessmentIcon />} iconPosition="start" sx={{ fontWeight: 700, textTransform: 'none' }} />
                    <Tab label="Participantes" icon={<PeopleIcon />} iconPosition="start" sx={{ fontWeight: 700, textTransform: 'none' }} />
                    <Tab label="Libro de Calificaciones" icon={<AssignmentIcon />} iconPosition="start" sx={{ fontWeight: 700, textTransform: 'none' }} />
                  </Tabs>
                </CardContent>
              </Card>

              {/* DETAILS TABS CONTENT */}
              {loadingDetails ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress color="secondary" />
                </Box>
              ) : (
                <Box>
                  {/* TAB 1: OVERVIEW & ANALYTICS */}
                  {tabValue === 0 && (
                    <Box>
                      {/* Metric Cards Grid */}
                      <Grid container spacing={3} mb={3}>
                        <Grid item xs={6} sm={3}>
                          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', borderRadius: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Estudiantes</Typography>
                            <Typography variant="h4" fontWeight={900} sx={{ color: '#0A2540', mt: 0.5 }}>{analytics.activeStudents}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', borderRadius: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Promedio Curso</Typography>
                            <Typography 
                              variant="h4" 
                              fontWeight={900} 
                              sx={{ 
                                color: typeof analytics.courseAvg === 'number' ? getGradeColor(analytics.courseAvg) : '#0A2540', 
                                mt: 0.5 
                              }}
                            >
                              {analytics.courseAvg}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', borderRadius: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Aprobación (Nota ≥ 50)</Typography>
                            <Typography variant="h4" fontWeight={900} color="secondary.main" sx={{ mt: 0.5 }}>{analytics.passRate}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', borderRadius: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Rango (Min - Max)</Typography>
                            <Typography variant="h5" fontWeight={900} sx={{ color: '#0A2540', mt: 1.2 }}>
                              {analytics.lowestGrade} - {analytics.highestGrade}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Analytics visual graph */}
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 4 }}>
                            <Typography variant="subtitle1" fontWeight={750} sx={{ color: '#0A2540', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TrendingUpIcon color="secondary" />
                              Distribución de Calificaciones de Estudiantes
                            </Typography>

                            {typeof analytics.courseAvg === 'string' ? (
                              <Box textAlign="center" py={6}>
                                <InfoIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No hay suficientes promedios para calcular la distribución.
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{ width: '100%', height: 260 }}>
                                <ResponsiveContainer>
                                  <BarChart data={analytics.gradeRanges} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                    <XAxis dataKey="name" fontSize={11} stroke="#94A3B8" />
                                    <YAxis fontSize={11} stroke="#94A3B8" allowDecimals={false} />
                                    <Tooltip 
                                      contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }} 
                                      labelStyle={{ fontWeight: 700 }}
                                    />
                                    <Bar dataKey="value" name="Alumnos" radius={[6, 6, 0, 0]} maxBarSize={60}>
                                      {analytics.gradeRanges.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* TAB 2: PARTICIPANTS DIRECTORY */}
                  {tabValue === 1 && (
                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                          <Typography variant="h6" fontWeight={750} sx={{ color: '#0A2540' }}>
                            Participantes Registrados ({enrollments.length})
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={handleExportCourseStudents}
                            startIcon={<FileDownloadIcon />}
                            sx={{ borderRadius: 30, px: 3, textTransform: 'none', color: '#00B074', borderColor: '#00B074', '&:hover': { borderColor: '#008C5C', bgcolor: 'rgba(0,176,116,0.04)' } }}
                          >
                            Exportar Alumnos Excel
                          </Button>
                        </Box>

                        {enrollments.length === 0 ? (
                          <Typography align="center" color="text.secondary" py={4}>
                            No hay alumnos ni participantes cargados en este curso.
                          </Typography>
                        ) : isMobile ? (
                          <Box display="flex" flexDirection="column" gap={2}>
                            {enrollments.map(enrol => (
                              <Card key={enrol.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Avatar src={enrol.avatar_url} sx={{ width: 44, height: 44 }}>
                                    {getInitials(enrol.user_name)}
                                  </Avatar>
                                  <Box flexGrow={1}>
                                    <Typography variant="subtitle2" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                                      {enrol.user_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.1 }}>
                                      {enrol.user_email}
                                    </Typography>
                                    <Box display="flex" gap={1} mt={1} alignItems="center">
                                      <Chip 
                                        label={enrol.role === 'student' ? 'Estudiante' : 'Docente'} 
                                        size="small" 
                                        color={enrol.role === 'student' ? 'secondary' : 'primary'} 
                                        variant="outlined" 
                                        sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }} 
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </Card>
                            ))}
                          </Box>
                        ) : (
                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                            <Table size="small">
                              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                <TableRow>
                                  <TableCell width={70}>Foto</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Nombre Completo</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Correo Electrónico</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Rol Moodle</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Inscrito</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {enrollments.map(enrol => (
                                  <TableRow key={enrol.id} hover>
                                    <TableCell>
                                      <Avatar src={enrol.avatar_url} sx={{ width: 36, height: 36, border: '1.5px solid #E2E8F0', bgcolor: 'rgba(0,112,224,0.05)', color: '#0070E0', fontWeight: 700 }}>
                                        {getInitials(enrol.user_name)}
                                      </Avatar>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontWeight={750} variant="body2" sx={{ color: '#0A2540' }}>
                                        {enrol.user_name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{enrol.user_email}</TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={enrol.role === 'student' ? 'Estudiante' : enrol.role === 'teacher' || enrol.role === 'editingteacher' ? 'Docente' : enrol.role} 
                                        size="small"
                                        color={enrol.role === 'student' ? 'secondary' : 'primary'}
                                        variant="outlined"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {enrol.time_enrolled ? new Date(enrol.time_enrolled).toLocaleDateString() : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* TAB 3: GRADEBOOK & MANUAL GRADING */}
                  {tabValue === 2 && (
                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                          <Typography variant="h6" fontWeight={750} sx={{ color: '#0A2540' }}>
                            Libro de Notas Académicas
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={handleExportCourseGrades}
                            startIcon={<FileDownloadIcon />}
                            sx={{ borderRadius: 30, px: 3, textTransform: 'none', color: '#00B074', borderColor: '#00B074', '&:hover': { borderColor: '#008C5C', bgcolor: 'rgba(0,176,116,0.04)' } }}
                          >
                            Exportar Libro de Calificaciones
                          </Button>
                        </Box>

                        {studentGradeSummaries.length === 0 ? (
                          <Typography align="center" color="text.secondary" py={4}>
                            No hay estudiantes evaluados en este curso.
                          </Typography>
                        ) : (
                          <Box display="flex" flexDirection="column" gap={2}>
                            {studentGradeSummaries.map(student => (
                              <Paper 
                                key={student.studentId}
                                variant="outlined"
                                sx={{
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                  borderColor: expandedStudentId === student.studentId ? 'secondary.main' : '#E2E8F0',
                                  transition: 'all 0.2s'
                                }}
                              >
                                {/* Header Summary Row */}
                                <Box 
                                  onClick={() => setExpandedStudentId(expandedStudentId === student.studentId ? null : student.studentId)}
                                  sx={{ 
                                    p: 2.2, 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    bgcolor: expandedStudentId === student.studentId ? 'rgba(0, 112, 224, 0.01)' : 'transparent',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    '&:hover': {
                                      bgcolor: '#F8FAFC'
                                    }
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={2} sx={{ alignSelf: 'flex-start' }}>
                                    <Avatar sx={{ width: 38, height: 38, bgcolor: 'secondary.main' }}>
                                      {getInitials(student.name)}
                                    </Avatar>
                                    <Box>
                                      <Typography fontWeight={800} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                                        {student.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {student.email} • Calificaciones: <strong>{student.gradedCount}</strong>
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box display="flex" alignItems="center" gap={3} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                                    <Box textAlign="right">
                                      <Typography variant="caption" color="text.secondary" display="block">Promedio General</Typography>
                                      <Typography 
                                        variant="h5" 
                                        fontWeight={900} 
                                        sx={{ color: getGradeColor(student.average), lineHeight: 1 }}
                                      >
                                        {student.average !== null ? student.average.toFixed(1) : '-'}
                                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94A3B8', marginLeft: 3 }}>/ 100</span>
                                      </Typography>
                                    </Box>
                                    <Button variant="text" size="small" color="secondary" sx={{ fontWeight: 700, textTransform: 'none' }}>
                                      {expandedStudentId === student.studentId ? 'Ocultar Detalle' : 'Ver Notas'}
                                    </Button>
                                  </Box>
                                </Box>

                                {/* Expanding Student Grade Items details */}
                                {expandedStudentId === student.studentId && (
                                  <Box sx={{ p: { xs: 2, sm: 3 }, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                      <Typography fontWeight={800} variant="body2" color="secondary">
                                        Desglose de Calificaciones Moodle
                                      </Typography>
                                      {isStaffOrAdmin && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="secondary"
                                          startIcon={<AddCircleOutlineIcon />}
                                          onClick={() => handleOpenCreateGrade(student.enrollmentId)}
                                          sx={{ borderRadius: 30, textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', px: 2 }}
                                        >
                                          Nota Manual
                                        </Button>
                                      )}
                                    </Box>

                                    {student.grades.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                                        No hay ítems registrados en CreaSync para este estudiante.
                                      </Typography>
                                    ) : (
                                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                                        <Table size="small">
                                          <TableHead sx={{ bgcolor: 'rgba(241, 245, 249, 0.8)' }}>
                                            <TableRow>
                                              <TableCell sx={{ fontWeight: 700 }}><Typography fontWeight={700} variant="caption">Evaluación / Actividad</Typography></TableCell>
                                              <TableCell align="right" sx={{ fontWeight: 700 }} width={80}><Typography fontWeight={700} variant="caption">Nota</Typography></TableCell>
                                              {isStaffOrAdmin && <TableCell align="center" sx={{ fontWeight: 700 }} width={60}><Typography fontWeight={700} variant="caption">Editar</Typography></TableCell>}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {student.grades.map(grade => {
                                              const chip = getGradeTypeChip(grade.item_type);
                                              return (
                                                <TableRow key={grade.id} hover>
                                                  <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                                      <Typography variant="caption" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                                                        {grade.item_name}
                                                      </Typography>
                                                      <Chip 
                                                        label={chip.label} 
                                                        size="small" 
                                                        sx={{ 
                                                          height: 18, 
                                                          fontSize: '0.62rem', 
                                                          fontWeight: 700, 
                                                          color: chip.color, 
                                                          backgroundColor: chip.bgColor,
                                                          border: `1px solid ${chip.color}25`,
                                                          textTransform: 'uppercase'
                                                        }} 
                                                      />
                                                    </Box>
                                                    
                                                    {(() => {
                                                      const clean = (grade.feedback || '')
                                                        .replace(/&nbsp;/g, ' ')
                                                        .replace(/<[^>]*>/g, '')
                                                        .trim();
                                                      return clean ? (
                                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.2 }}>
                                                          Retroalimentación: {clean}
                                                        </Typography>
                                                      ) : null;
                                                    })()}
                                                    
                                                    {grade.date_graded && (
                                                      <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.62rem', mt: 0.2 }}>
                                                        Sincronizado: {new Date(grade.date_graded).toLocaleDateString()}
                                                      </Typography>
                                                    )}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Typography variant="body2" fontWeight={850} sx={{ color: getGradeColor(grade.grade) }}>
                                                      {grade.grade !== null ? grade.grade.toFixed(1) : '-'}
                                                    </Typography>
                                                  </TableCell>
                                                  {isStaffOrAdmin && (
                                                    <TableCell align="center">
                                                      <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={() => handleOpenEditGrade(student.enrollmentId, grade)}
                                                      >
                                                        <EditIcon fontSize="inherit" />
                                                      </IconButton>
                                                    </TableCell>
                                                  )}
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* GRADE EDIT / CREATE MANUAL MODAL FORM */}
      <Dialog 
        open={gradeDialogOpen} 
        onClose={() => !savingGrade && setGradeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0A2540' }}>
            {editingGrade ? 'Editar Calificación' : 'Cargar Nota Manual'}
          </Typography>
          <IconButton onClick={() => setGradeDialogOpen(false)} disabled={savingGrade}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleGradeFormSubmit}>
          <DialogContent dividers sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Evaluación / Actividad"
                  name="item_name"
                  value={gradeForm.item_name}
                  onChange={handleGradeFormChange}
                  required
                  disabled={!!editingGrade}
                  placeholder="ej: Tarea Moodle Local, Participación Clase"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Calificación / Nota (0 - 100)"
                  name="grade"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={gradeForm.grade}
                  onChange={handleGradeFormChange}
                  required
                  placeholder="ej: 85.0"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Retroalimentación / Observación"
                  name="feedback"
                  multiline
                  rows={3}
                  value={gradeForm.feedback}
                  onChange={handleGradeFormChange}
                  placeholder="Ingresa comentarios o retroalimentación sobre el alumno..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setGradeDialogOpen(false)} color="inherit" disabled={savingGrade}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={savingGrade}
              sx={{ borderRadius: 30, px: 4 }}
            >
              {savingGrade ? <CircularProgress size={20} color="inherit" /> : 'Guardar Nota'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CoursesPage;
